/**
 * Controlled email-OTP verification screen (native-auth styling).
 * Shared by SSPR (verify reset code) and MFA (verify second factor). The code is
 * already sent by the backend before this renders, so there is no "send" button;
 * "resend" is optional and gated by a cooldown.
 *
 * Text comes from the app-wide react-intl catalogue (AUTH.NATIVE.* keys).
 */
import {FormEvent, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

interface OtpScreenProps {
  title?: string;
  sentTo?: string | null;
  /** Overrides the default "code sent to X" line (e.g. SSPR's neutral message). */
  infoMessage?: string;
  verifying?: boolean;
  error?: string;
  onSubmit: (code: string) => void;
  onResend?: () => void;
  resendCooldownSeconds?: number;
}

const OtpScreen = ({
  title,
  sentTo,
  infoMessage,
  verifying = false,
  error,
  onSubmit,
  onResend,
  resendCooldownSeconds = 30,
}: OtpScreenProps): JSX.Element => {
  const intl = useIntl();
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(resendCooldownSeconds);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = setInterval(() => setCooldown((value) => Math.max(value - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    onSubmit(code.trim());
  };

  const handleResend = (): void => {
    if (cooldown > 0 || !onResend) {
      return;
    }
    onResend();
    setCooldown(resendCooldownSeconds);
  };

  const heading = title ?? intl.formatMessage({id: 'AUTH.NATIVE.VERIFICATION'});
  const codeLabel = intl.formatMessage({id: 'AUTH.NATIVE.OTP.PLACEHOLDER'});
  const defaultInfo = intl.formatMessage(
    {id: 'AUTH.NATIVE.OTP.INFO'},
    {sentTo: sentTo || intl.formatMessage({id: 'AUTH.NATIVE.OTP.YOUR_EMAIL'})}
  );

  return (
    <form className="native-form" onSubmit={handleSubmit} aria-label={heading}>
      <h2 className="native-title">{heading}</h2>
      <p className="native-subtitle" role="alert" aria-live="polite">
        {infoMessage ?? defaultInfo}
      </p>

      {error ? (
        <div className="error pageLevel" role="alert">
          <p className="mb-0">{error}</p>
        </div>
      ) : null}

      <div className="native-field">
        <input
          className={error ? 'native-otp-input input-error' : 'native-otp-input'}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={codeLabel}
          aria-label={codeLabel}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoFocus
        />
      </div>

      <button
        type="submit"
        className="native-submit"
        disabled={verifying || code.trim().length === 0}
      >
        {verifying
          ? intl.formatMessage({id: 'AUTH.NATIVE.OTP.SUBMITTING'})
          : intl.formatMessage({id: 'AUTH.NATIVE.OTP.SUBMIT'})}
      </button>

      {onResend ? (
        <button
          type="button"
          className="native-resend"
          onClick={handleResend}
          disabled={cooldown > 0}
        >
          {cooldown > 0
            ? intl.formatMessage({id: 'AUTH.NATIVE.OTP.RESEND_COOLDOWN'}, {seconds: cooldown})
            : intl.formatMessage({id: 'AUTH.NATIVE.OTP.RESEND'})}
        </button>
      ) : null}
    </form>
  );
};

export default OtpScreen;
